/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import { Editor } from "@tinymce/tinymce-react";
import { useUI } from "state/UIContext";
import Text from "components/Text";
import { useDirection } from "hooks/useDirection";

const SCRIPT_SRC = `${import.meta.env.BASE_URL}tinymce/tinymce.min.js`;

export const addEditorTagPlaceholder = (editor, tag) => {
  editor.insertContent(`&nbsp;<span>${tag}</span>&nbsp;`);
};

const RichTextEditor = forwardRef(
  ({ onChange, value, disabled, onAddButton, id, maxLength }, ref) => {
    const [length, setLength] = useState(0);
    const { direction, language } = useDirection();
    const handleEditorChange = (content, editor) => {
      const length = editor.getContent({ format: "text" }).length;
      if ((maxLength && length <= maxLength) || !maxLength) {
        setLength(length);
        onChange?.(content);
      } else {
        const container = editor.editorContainer;
        container.style.border = "2px solid var(--alert)";
        setTimeout(() => {
          container.style.border = "";
        }, 500);
      }
    };

    const divRef = useRef();

    useImperativeHandle(ref, () => {
      return {
        focus() {
          divRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        },
      };
    }, []);

    const handleBeforeAddUndo = (evt, editor) => {
      const length = editor.getContent({ format: "text" }).length;
      if (length > maxLength) {
        evt.preventDefault();
      }
    };
    const { colorScheme } = useUI();
    const bgStyle =
      colorScheme === "dark"
        ? "body{background-color: #181818; color: #fff;}"
        : "body{background-color: #FFF; color: #000;}";

    return (
      <div ref={divRef} className="relative">
        <Editor
          id={id}
          tinymceScriptSrc={SCRIPT_SRC}
          disabled={disabled}
          ref={(editor) => {
            if (editor && ref) {
              if (editor?.hasOwnProperty("editor")) {
                ref.current = editor.editor;
              } else {
                ref.current = editor;
              }
            }
          }}
          onInit={(_evt, editor) => {
            setLength(editor.getContent({ format: "text" }).length);

            if (editor && ref) {
              if (editor?.hasOwnProperty("editor")) {
                // @ts-ignore
                ref.current = editor;
              } else {
                // @ts-ignore
                ref.current = editor;
              }
            }
          }}
          onBeforeAddUndo={maxLength ? handleBeforeAddUndo : undefined}
          value={value || ""}
          init={{
            skin_url:
              colorScheme === "dark"
                ? "/skins/ui/oxide-dark"
                : "/skins/ui/oxide",
            content_css:
              colorScheme === "dark"
                ? ["/skins/content/default/content.css"]
                : ["/skins/content/dark/content.css"],
            content_style: bgStyle,
            height: 360,
            branding: false,
            directionality: direction,
            language: language === "ar" ? "ar" : "en",
            language_url:
              language === "ar" ? "/tinymce/langs/ar.js" : undefined,
            promotion: false,
            plugins:
              "preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media codesample table charmap pagebreak nonbreaking insertdatetime advlist lists wordcount help charmap quickbars",
            menubar: "file edit view insert format tools table",
            // toolbar_sticky: true,
            setup: (editor) => {
              if (onAddButton) {
                editor.ui.registry.addButton("ADD_BTN", {
                  icon: "plus",
                  tooltip: "Add tag",
                  onAction(_) {
                    onAddButton(editor);
                  },
                });
              }
            },
            // Don't add `fullscreen` to the toolbar. It's buggy.
            toolbar: `undo redo | bold italic underline strikethrough |${
              onAddButton && " ADD_BTN |"
            } fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat | pagebreak | charmap | preview save print | link codesample | ltr rtl`,
          }}
          onEditorChange={handleEditorChange}
          // Doesn't work properly
          // onBlur={(editor) => {
          //   const content = editor.target.getContent();
          //   rest.onBlur?.(content);
          // }}
          test-id="8yV2TXKB"
        />
        {maxLength && (
          <Text
            as="span"
            className=" text-[12px] ml-auto text-[#7a7a7a] absolute right-0.5 -bottom-5"
          >
            {length ?? 0} / {maxLength}
          </Text>
        )}
      </div>
    );
  },
);

export default RichTextEditor;
